
import { useState, useRef } from 'react'

const MemberCaseStudiesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const touchStartX = useRef<number>(0)
  const touchEndX = useRef<number>(0)

  const caseStudies = [
    {
      title: "A Tale of Two Weddings",
      content: "We were commissioned to orchestrate an extraordinary wedding celebration for a bride and groom wishing to unite both heritage and sophistication across Shanghai and Paris. The couple shared their dream with us: she envisioned honoring her Chinese roots with a traditional ceremony, as well as experiencing the elegance of a classic Western wedding.\n\nIn Shanghai, we secured a stunning historic venue adorned with vibrant red decor and shimmering lanterns. The day began with a beautifully coordinated tea ceremony, the bride dressed in a custom crafted qipao, surrounded by family traditions and festive celebrations. Every detail, from auspicious motifs to a lavish banquet, paid homage to her cultural heritage.\n\nFor Paris, our team transformed Château de Chantilly chateau into a picturesque romantic setting for their Western celebration. The bride made her entrance in a bespoke Saint Laurent gown— elegant, timeless, and crafted just for her. The ceremony was followed by a candlelit dinner featuring classic French cuisine and live music, creating a magical atmosphere.",
      image: "/Landing Page/Member Case Studies/01-wedding.png"
    },
    {
      title: "Worldwide University Access",
      content: "A family approached us seeking expert support for their child's dream: to study Physics at a world-class university in the UK. Our global education specialists crafted a tailored roadmap—curating a suite of preparatory enrichment, mentoring, and admissions guidance at every stage. Throughout the journey, we orchestrated masterclasses with university alumni, practice interviews led by former admissions officers, and one-on-one test preparation that addressed both academics and the nuances of the Imperial College admissions process.\n\nBeyond documents and deadlines, every detail was considered: from providing insight into the UK education landscape, to arranging a virtual campus tour that inspired and motivated. With our unwavering support, the student's application reflected not only academic excellence, but also a genuine passion for science and global citizenship. The result—an offer from Imperial College London for the Bachelor of Physics, and a future bright with opportunity.",
      image: "/Landing Page/Member Case Studies/02-university.png"
    },
    {
      title: "France in Full Bloom",
      content: "We curated an unforgettable one-month journey for a client wishing to immerse themselves in the best of France's cities and countryside. Beginning in Paris, we arranged a suite at the iconic Hôtel Plaza Athénée, setting the stage with private tours of the Louvre and sunset dinners overlooking the Seine. The adventure continued through Provence's lavender fields and charming villages, with bespoke stays in château hotels offering hands-on culinary classes and exclusive farm-to-table tasting menus.\n\nFor a taste of Riviera glamour, we secured coveted rooms at Hotel de Paris Monte-Carlo, where the client enjoyed evenings at Michelin-starred restaurants and thrilling nights at the legendary casino. In Bordeaux, our specialists organized a week of immersive wine education—private vineyard visits, tutored tastings guided by leading sommeliers, and masterclasses in French wine terroir.\n\nFrom gourmet bistros in Lyon to freshly shucked oysters in Arcachon, every meal was a discovery, each hotel a sanctuary of luxury. The client finished their month with sunsets from a private terrace in Nice, deeply enriched by the flavors, culture, and elegance that only FGA Prestige's connections could deliver.",
      image: "/Landing Page/Member Case Studies/03-France.png"
    },
    {
      title: "Reservation Against the Odds",
      content: "When a client needed to secure a last-minute dinner reservation for a high-stakes business guest in New York City, they turned to us for help. With just hours to spare and top restaurants fully booked, our team leveraged our expertise and connections to make the impossible happen.\n\nUnderstanding the significance of the meeting, we arranged a table at Eleven Madison Park—a three Michelin-starred icon celebrated for its refined, plant-based tasting menu and world-class hospitality, making it one of the most sought-after reservations in the city. The client and their guest experienced an unforgettable meal overlooking Madison Square Park, with flawless service that set the ideal tone for their business discussion.",
      image: "/Landing Page/Member Case Studies/04-Reservation.png"
    },
    {
      title: "Acquisition of Historic AI Artwork",
      content: "A surprise birthday dinner where the venue was transformed with exquisite floral installations for a magical evening on the Gaddi's Peninsula. Guests enjoyed sunset cocktails overlooking the water, followed by a curated multi-course gourmet dinner. The celebration featured live acoustic performances and a customized cake presentation. Every décor element, from the tablescapes to the lighting, was designed to reflect the guest of honor's personal style, ensuring an elegant and unforgettable birthday experience.",
      image: "/Landing Page/Member Case Studies/05-Acquisition.png"
    },
    {
      title: "Diamond Jubilee Birthday Party",
      content: "A surprise birthday dinner where the venue was transformed with exquisite floral installations for a magical evening on the Gaddi's Peninsula. Guests enjoyed sunset cocktails overlooking the water, followed by a curated multi-course gourmet dinner. The celebration featured live acoustic performances and a customized cake presentation. Every décor element, from the tablescapes to the lighting, was designed to reflect the guest of honor's personal style, ensuring an elegant and unforgettable birthday experience.",
      image: "/Landing Page/Member Case Studies/06-BirthdayParty.png"
    }
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % caseStudies.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + caseStudies.length) % caseStudies.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  // Handle touch events for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return
    
    const distance = touchStartX.current - touchEndX.current
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      nextSlide()
    }
    if (isRightSwipe) {
      prevSlide()
    }
  }

  return (
    <section
      className="w-full flex flex-col lg:flex-row min-h-[400px] lg:min-h-[560px]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Image Column */}
      <div
        className="w-full lg:flex-1 aspect-square md:aspect-auto md:h-80 lg:h-[560px]"
        style={{
          backgroundImage: `url("${caseStudies[currentSlide].image}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Content Column */}
      <div
        className="w-full lg:flex-1 bg-black px-6 py-8 md:px-12 md:py-12 lg:px-14 lg:py-12 flex flex-col justify-between text-center lg:h-[560px]"
      >
        {/* Content Area */}
        <div className="flex-1 flex flex-col justify-center">
          {/* Section Header */}
          <p
            className="text-lg md:text-xl lg:text-2xl mb-4 md:mb-5 italic"
            style={{
              fontFamily: 'Cardo, serif',
              color: '#CFBF9E',
              textAlign: 'center'
            }}
          >
            Member Case Studies
          </p>
          
          {/* Title */}
          <h2
            className="text-2xl md:text-3xl lg:text-4xl mb-4 md:mb-5 font-normal"
            style={{
              fontFamily: 'Cardo, serif',
              color: '#FFFFFF',
              textAlign: 'center'
            }}
          >
            {caseStudies[currentSlide].title}
          </h2>
          
          {/* Content */}
          <div
            className="text-xs md:text-sm leading-relaxed"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              color: '#FFFFFF',
              textAlign: 'center'
            }}
          >
            {caseStudies[currentSlide].content.split('\n\n').map((paragraph, index) => (
              <p key={index} className={`${index < caseStudies[currentSlide].content.split('\n\n').length - 1 ? 'mb-4 md:mb-5' : ''}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between mt-6 lg:mt-0">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="w-8 h-8 md:w-10 md:h-10 border-none bg-transparent text-white flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Previous case study"
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Slide Indicator */}
          <div className="flex items-center gap-2">
            <span
              className="text-sm text-white font-normal"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif'
              }}
            >
              {String(currentSlide + 1).padStart(2, '0')}
            </span>
            
            <div className="w-16 md:w-20 h-px bg-gray-700 relative">
              <div
                className="h-full transition-all duration-300 ease-out"
                style={{
                  backgroundColor: '#CFBF9E',
                  width: `${((currentSlide + 1) / caseStudies.length) * 100}%`
                }}
              />
            </div>
            
            <span
              className="text-sm font-normal"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                color: 'rgba(255, 255, 255, 0.6)'
              }}
            >
              {String(caseStudies.length).padStart(2, '0')}
            </span>
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="w-8 h-8 md:w-10 md:h-10 border-none bg-transparent text-white flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Next case study"
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  )
}

export default MemberCaseStudiesSection